import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { format, isSameDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { Badge } from '@mui/material';
import { db } from '../../firebase/config';
import { collection, query, where, getDocs, addDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { COLLECTIONS } from '../../firebase/collections';

// Liste des types d'interventions courantes
const INTERVENTION_TYPES = [
  'Réparation électrique',
  'Réparation plomberie',
  'Maintenance climatisation',
  'Réparation menuiserie',
  'Peinture et décoration',
  'Nettoyage et entretien',
  'Installation équipement',
  'Réparation informatique',
  'Maintenance réseau',
  'Réparation chauffage',
  'Travaux de maçonnerie',
  'Réparation toiture',
  'Entretien espaces verts',
  'Réparation ascenseur',
  'Contrôle de sécurité',
  'Autre'
];

const InterventionCalendar = () => {
  console.log('InterventionCalendar: Initialisation du composant');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [interventions, setInterventions] = useState([]);
  const [workRequests, setWorkRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(0); // 0 = Liste, 1 = Tableau
  const [periodFilter, setPeriodFilter] = useState('day'); // 'day', 'week', 'month'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    time: '09:00',
    duration: 60,
    location: '',
    workRequestId: '',
    equipment: '',
    technicianName: ''
  });
  const [serviceInfo, setServiceInfo] = useState(null);
  const { currentUser, userService, userRole } = useAuth();
  
  console.log('InterventionCalendar: État initial', {
    currentUser: currentUser?.uid,
    userRole,
    userService,
    loading,
    error
  });

  useEffect(() => {
    console.log('InterventionCalendar: useEffect[currentUser]', {
      currentUser: currentUser?.uid,
      userRole,
      userService
    });
    if (currentUser) {
      fetchServiceInfo();
    } else {
      console.log('InterventionCalendar: Pas d\'utilisateur connecté');
    }
  }, [currentUser, userRole, userService]);
  
  const fetchServiceInfo = async () => {
    console.log('InterventionCalendar: Début fetchServiceInfo', {
      COLLECTIONS: JSON.stringify(COLLECTIONS),
      db: db ? 'DB disponible' : 'DB non disponible'
    });
    try {
      setLoading(true);
      
      // Si userService est déjà disponible dans le contexte d'authentification
      if (userService) {
        console.log('InterventionCalendar: userService disponible dans le contexte', userService);
        setServiceInfo(userService);
        fetchInterventions();
        return;
      }
      
      console.log('InterventionCalendar: userService non disponible, vérification du rôle', userRole);
      
      // Pour les utilisateurs avec le rôle "technical", on récupère directement leur service
      if (currentUser && userRole === 'technical') {
        console.log('InterventionCalendar: Récupération du service pour un utilisateur technique', {
          uid: currentUser.uid,
          userRole,
          COLLECTIONS: COLLECTIONS
        });
        
        // Récupérer d'abord les données de l'utilisateur
        console.log('InterventionCalendar: Tentative de récupération du document utilisateur', {
          collection: COLLECTIONS.USERS,
          uid: currentUser.uid
        });
        const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, currentUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('InterventionCalendar: Données utilisateur récupérées', {
            userData,
            serviceId: userData.serviceId,
            role: userData.role
          });
          
          if (userData.serviceId) {
            console.log('InterventionCalendar: ServiceId trouvé', {
              serviceId: userData.serviceId,
              collection: COLLECTIONS.SERVICES
            });
            const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, userData.serviceId));
            
            if (serviceDoc.exists()) {
              const serviceData = {
                id: serviceDoc.id,
                ...serviceDoc.data()
              };
              console.log('InterventionCalendar: Service récupéré avec succès', serviceData);
              setServiceInfo(serviceData);
              fetchInterventions();
            } else {
              console.error('InterventionCalendar: Service non trouvé dans Firestore', {
                serviceId: userData.serviceId,
                collection: COLLECTIONS.SERVICES
              });
              // Créer un service par défaut pour éviter l'erreur
              const defaultService = { id: currentUser.uid, name: 'Service Technique' };
              console.log('InterventionCalendar: Utilisation d\'un service par défaut', defaultService);
              setServiceInfo(defaultService);
              fetchInterventions();
            }
          } else {
            console.log('InterventionCalendar: Aucun serviceId trouvé dans les données utilisateur');
            // Utiliser l'ID de l'utilisateur comme ID de service par défaut
            const defaultService = { id: currentUser.uid, name: 'Service Technique' };
            console.log('InterventionCalendar: Utilisation de l\'ID utilisateur comme fallback', defaultService);
            setServiceInfo(defaultService);
            fetchInterventions();
          }
        } else {
          console.error('InterventionCalendar: Utilisateur non trouvé dans Firestore', {
            uid: currentUser.uid,
            collection: COLLECTIONS.USERS
          });
          // Créer un service par défaut pour éviter l'erreur
          const defaultService = { id: currentUser.uid, name: 'Service Technique' };
          console.log('InterventionCalendar: Création d\'un service par défaut', defaultService);
          setServiceInfo(defaultService);
          fetchInterventions();
        }
      } else if (userRole === 'super_admin') {
        // Les super_admin n'ont pas besoin d'un service spécifique
        console.log('InterventionCalendar: Utilisateur super_admin détecté');
        const adminService = { id: 'admin', name: 'Administration' };
        console.log('InterventionCalendar: Utilisation du service admin', adminService);
        setServiceInfo(adminService);
        fetchInterventions();
      } else {
        // Pour les autres rôles, on utilise un service par défaut
        console.log('InterventionCalendar: Autre rôle détecté', {
          userRole,
          currentUser: currentUser?.uid
        });
        const defaultService = { id: currentUser?.uid || 'default', name: 'Service' };
        console.log('InterventionCalendar: Utilisation d\'un service par défaut pour autre rôle', defaultService);
        setServiceInfo(defaultService);
        fetchInterventions();
      }
    } catch (error) {
      console.error('InterventionCalendar: Erreur lors de la récupération des informations du service', {
        error: error.message,
        stack: error.stack
      });
      // En cas d'erreur, on utilise un service par défaut pour éviter l'affichage de l'erreur
      const fallbackService = { id: currentUser?.uid || 'default', name: 'Service' };
      console.log('InterventionCalendar: Utilisation d\'un service de secours après erreur', fallbackService);
      setServiceInfo(fallbackService);
      fetchInterventions();
    } finally {
      console.log('InterventionCalendar: Fin de fetchServiceInfo', {
        serviceInfo,
        error
      });
      setLoading(false);
    }
  };

  const fetchInterventions = async () => {
    console.log('InterventionCalendar: Début fetchInterventions', {
      serviceInfo,
      COLLECTIONS: JSON.stringify(COLLECTIONS)
    });
    try {
      setLoading(true);
      setError('');
      
      // Vérifier que la collection existe et n'est pas vide
      if (!COLLECTIONS.INTERVENTIONS) {
        console.error('InterventionCalendar: La constante COLLECTIONS.INTERVENTIONS n\'est pas définie');
        setError('Erreur de configuration: collection des interventions non définie');
        setLoading(false);
        return;
      }
      
      console.log('InterventionCalendar: Tentative de récupération des interventions', {
        collection: COLLECTIONS.INTERVENTIONS
      });

      const q = query(collection(db, COLLECTIONS.INTERVENTIONS));
      const querySnapshot = await getDocs(q);
      console.log('InterventionCalendar: Nombre d\'interventions récupérées', querySnapshot.size);
      
      const interventionsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || null
      }));
      
      console.log('InterventionCalendar: Données des interventions transformées', {
        count: interventionsData.length,
        sample: interventionsData.length > 0 ? interventionsData[0] : null
      });
      
      setInterventions(interventionsData);
      
      // Récupérer aussi les demandes en cours
      await fetchWorkRequests();
    } catch (error) {
      console.error('InterventionCalendar: Erreur lors de la récupération des interventions', {
        error: error.message,
        stack: error.stack
      });
      setError('Erreur lors du chargement des interventions');
    } finally {
      console.log('InterventionCalendar: Fin de fetchInterventions');
      setLoading(false);
    }
  };

  const fetchWorkRequests = async () => {
    try {
      console.log('InterventionCalendar: Récupération des demandes en cours');
      const q = query(
        collection(db, COLLECTIONS.WORK_REQUESTS),
        where('status', 'in', ['En attente', 'En cours', 'pending', 'in_progress'])
      );
      const querySnapshot = await getDocs(q);
      const requestsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('InterventionCalendar: Demandes en cours récupérées', requestsData.length);
      setWorkRequests(requestsData);
    } catch (error) {
      console.error('InterventionCalendar: Erreur lors de la récupération des demandes', error);
    }
  };

  const handleDateChange = (newDate) => {
    console.log('InterventionCalendar: Changement de date', {
      previousDate: selectedDate,
      newDate
    });
    setSelectedDate(newDate);
  };

  const handlePrintIntervention = (intervention) => {
    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    
    // Générer le HTML de la fiche d'intervention
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Fiche d'Intervention - ${intervention.title}</title>
          <style>
            @media print {
              @page {
                margin: 2cm;
              }
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              border-bottom: 3px solid #1976d2;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .header h1 {
              color: #1976d2;
              margin: 0;
              font-size: 28px;
            }
            .header p {
              color: #666;
              margin: 5px 0;
            }
            .section {
              margin-bottom: 25px;
              border: 1px solid #ddd;
              padding: 15px;
              border-radius: 5px;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 15px;
              border-bottom: 2px solid #1976d2;
              padding-bottom: 5px;
            }
            .info-row {
              display: flex;
              margin-bottom: 10px;
              padding: 8px;
              background-color: #f5f5f5;
              border-radius: 3px;
            }
            .info-label {
              font-weight: bold;
              min-width: 180px;
              color: #333;
            }
            .info-value {
              color: #666;
            }
            .signature-section {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
            }
            .signature-box {
              width: 45%;
              border-top: 2px solid #333;
              padding-top: 10px;
              text-align: center;
            }
            .notes-section {
              min-height: 150px;
              border: 1px solid #ddd;
              padding: 15px;
              border-radius: 5px;
              background-color: #fafafa;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #999;
              border-top: 1px solid #ddd;
              padding-top: 15px;
            }
            .print-button {
              display: block;
              margin: 20px auto;
              padding: 10px 30px;
              background-color: #1976d2;
              color: white;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-size: 16px;
            }
            .print-button:hover {
              background-color: #1565c0;
            }
            @media print {
              .print-button {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>FICHE D'INTERVENTION</h1>
            <p>Service Technique</p>
            <p>Date d'impression: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr })}</p>
          </div>

          <div class="section">
            <div class="section-title">Informations de l'intervention</div>
            <div class="info-row">
              <span class="info-label">Titre:</span>
              <span class="info-value">${intervention.title || 'Non spécifié'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date:</span>
              <span class="info-value">${intervention.date ? format(intervention.date, 'dd/MM/yyyy', { locale: fr }) : 'Non spécifiée'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Heure:</span>
              <span class="info-value">${intervention.date ? format(intervention.date, 'HH:mm', { locale: fr }) : 'Non spécifiée'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Durée estimée:</span>
              <span class="info-value">${intervention.duration} minutes</span>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Localisation</div>
            <div class="info-row">
              <span class="info-label">Lieu:</span>
              <span class="info-value">${intervention.location || 'Non spécifié'}</span>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Technicien assigné</div>
            <div class="info-row">
              <span class="info-label">Nom:</span>
              <span class="info-value">${intervention.technicianName || 'Non assigné'}</span>
            </div>
          </div>

          ${intervention.equipment ? `
          <div class="section">
            <div class="section-title">Équipement/Matériel nécessaire</div>
            <div class="info-row">
              <span class="info-value">${intervention.equipment}</span>
            </div>
          </div>
          ` : ''}

          ${intervention.description ? `
          <div class="section">
            <div class="section-title">Description</div>
            <div class="info-value" style="padding: 10px;">${intervention.description}</div>
          </div>
          ` : ''}

          <div class="section">
            <div class="section-title">Notes et observations</div>
            <div class="notes-section">
              <!-- Espace pour les notes manuscrites -->
            </div>
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <strong>Signature du technicien</strong>
            </div>
            <div class="signature-box">
              <strong>Signature du demandeur</strong>
            </div>
          </div>

          <div class="footer">
            Document généré automatiquement - EduWorks - Système de gestion des demandes de travaux
          </div>

          <button class="print-button" onclick="window.print()">Imprimer cette fiche</button>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleOpenDialog = () => {
    console.log('InterventionCalendar: Ouverture du dialogue de planification');
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setFormData({
      title: '',
      description: '',
      time: '09:00',
      duration: 60,
      location: '',
      workRequestId: '',
      equipment: '',
      technicianName: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    console.log('InterventionCalendar: Tentative de soumission du formulaire', formData);
    if (!formData.title || !formData.time || !formData.location) {
      console.log('InterventionCalendar: Formulaire incomplet, champs obligatoires manquants');
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Créer la date complète à partir de la date sélectionnée et de l'heure
      const [hours, minutes] = formData.time.split(':').map(Number);
      const interventionDate = new Date(selectedDate);
      interventionDate.setHours(hours, minutes, 0);
      console.log('InterventionCalendar: Date d\'intervention calculée', {
        selectedDate,
        time: formData.time,
        calculatedDate: interventionDate
      });

      // Vérifier que la collection existe et n'est pas vide
      if (!COLLECTIONS.INTERVENTIONS) {
        console.error('InterventionCalendar: La constante COLLECTIONS.INTERVENTIONS n\'est pas définie');
        setError('Erreur de configuration: collection des interventions non définie');
        setLoading(false);
        return;
      }
      
      // Créer une nouvelle intervention dans Firestore
      console.log('InterventionCalendar: Tentative d\'ajout d\'intervention dans', COLLECTIONS.INTERVENTIONS);
      
      const interventionData = {
        title: formData.title,
        description: formData.description,
        date: interventionDate,
        duration: Number(formData.duration),
        location: formData.location,
        workRequestId: formData.workRequestId || null,
        equipment: formData.equipment || '',
        technicianName: formData.technicianName || 'Non assigné',
        status: 'planned',
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        serviceId: serviceInfo?.id || currentUser.uid
      };
      
      console.log('InterventionCalendar: Données d\'intervention à ajouter', interventionData);
      
      await addDoc(collection(db, COLLECTIONS.INTERVENTIONS), interventionData);
      console.log('InterventionCalendar: Intervention ajoutée avec succès');

      handleCloseDialog();
      fetchInterventions();
    } catch (error) {
      console.error('InterventionCalendar: Erreur lors de la création de l\'intervention', {
        error: error.message,
        stack: error.stack
      });
      setError('Erreur lors de la création de l\'intervention');
    } finally {
      console.log('InterventionCalendar: Fin de la tentative de création d\'intervention');
      setLoading(false);
    }
  };

  // Filtrer les interventions selon la période sélectionnée
  const getFilteredInterventions = () => {
    if (periodFilter === 'day') {
      return interventions.filter(
        intervention => intervention.date && isSameDay(intervention.date, selectedDate)
      );
    } else if (periodFilter === 'week') {
      const weekStart = startOfWeek(selectedDate, { locale: fr });
      const weekEnd = endOfWeek(selectedDate, { locale: fr });
      return interventions.filter(
        intervention => intervention.date && isWithinInterval(intervention.date, { start: weekStart, end: weekEnd })
      ).sort((a, b) => a.date - b.date);
    } else if (periodFilter === 'month') {
      const monthStart = startOfMonth(selectedDate);
      const monthEnd = endOfMonth(selectedDate);
      return interventions.filter(
        intervention => intervention.date && isWithinInterval(intervention.date, { start: monthStart, end: monthEnd })
      ).sort((a, b) => a.date - b.date);
    }
    return [];
  };

  const interventionsForSelectedDate = getFilteredInterventions();

  // Fonction pour vérifier si une date a des interventions
  const hasInterventions = (date) => {
    return interventions.some(intervention => 
      intervention.date && isSameDay(intervention.date, date)
    );
  };

  // Fonction pour personnaliser l'affichage des jours dans le calendrier
  const ServerDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const hasIntervention = !outsideCurrentMonth && hasInterventions(day);

    return (
      <Badge
        key={day.toString()}
        overlap="circular"
        badgeContent={hasIntervention ? '●' : undefined}
        color="primary"
      >
        <PickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
          sx={{
            ...(hasIntervention && {
              backgroundColor: 'primary.light',
              color: 'primary.contrastText',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
              fontWeight: 'bold'
            })
          }}
        />
      </Badge>
    );
  };

  // Ajouter un log pour le rendu du composant
  console.log('InterventionCalendar: Rendu du composant', {
    loading,
    error,
    serviceInfo,
    interventionsCount: interventions.length,
    selectedDate
  });

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Planification des interventions
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Cette fonctionnalité permet de planifier les interventions sur un calendrier et d'optimiser les déplacements.
      </Typography>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}
      
      {/* Ajouter un indicateur de débogage pour voir l'état du service */}
      <Box sx={{ display: 'none' }}>
        <pre>{JSON.stringify({ userRole, serviceInfo }, null, 2)}</pre>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
              <DateCalendar 
                value={selectedDate}
                onChange={handleDateChange}
                slots={{
                  day: ServerDay,
                }}
                sx={{ width: '100%' }}
              />
            </LocalizationProvider>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleOpenDialog}
              >
                Planifier une intervention
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                {periodFilter === 'day' && `Interventions du ${format(selectedDate, 'dd MMMM yyyy', { locale: fr })}`}
                {periodFilter === 'week' && `Semaine du ${format(startOfWeek(selectedDate, { locale: fr }), 'dd MMM', { locale: fr })} au ${format(endOfWeek(selectedDate, { locale: fr }), 'dd MMM yyyy', { locale: fr })}`}
                {periodFilter === 'month' && `Mois de ${format(selectedDate, 'MMMM yyyy', { locale: fr })}`}
              </Typography>
              <Chip 
                label={`${interventionsForSelectedDate.length} intervention(s)`} 
                color="primary" 
                size="small"
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
              <Tabs value={viewMode} onChange={(e, newValue) => setViewMode(newValue)}>
                <Tab label="Vue Liste" />
                <Tab label="Vue Tableau" />
              </Tabs>

              <ToggleButtonGroup
                value={periodFilter}
                exclusive
                onChange={(e, newValue) => newValue && setPeriodFilter(newValue)}
                size="small"
                color="primary"
              >
                <ToggleButton value="day">Jour</ToggleButton>
                <ToggleButton value="week">Semaine</ToggleButton>
                <ToggleButton value="month">Mois</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {loading && <CircularProgress size={24} sx={{ mt: 2 }} />}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Vue Liste */}
            {viewMode === 0 && (
              interventionsForSelectedDate.length > 0 ? (
                interventionsForSelectedDate.map((intervention) => (
                  <Box key={intervention.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, position: 'relative' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                        {intervention.title}
                      </Typography>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handlePrintIntervention(intervention)}
                        title="Imprimer la fiche d'intervention"
                      >
                        <PrintIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {intervention.date ? format(intervention.date, 'HH:mm', { locale: fr }) : 'Non spécifiée'} ({intervention.duration} min)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {intervention.location}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {intervention.technicianName || 'Non assigné'}
                      </Typography>
                    </Box>
                    {intervention.equipment && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <BuildIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {intervention.equipment}
                        </Typography>
                      </Box>
                    )}
                    {intervention.description && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {intervention.description}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Aucune intervention planifiée pour cette date
                </Typography>
              )
            )}

            {/* Vue Tableau */}
            {viewMode === 1 && (
              interventionsForSelectedDate.length > 0 ? (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {periodFilter !== 'day' && <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>}
                        <TableCell sx={{ fontWeight: 'bold' }}>Heure</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Intervention</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Lieu</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Technicien</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Durée</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interventionsForSelectedDate.map((intervention) => (
                        <TableRow key={intervention.id} hover>
                          {periodFilter !== 'day' && (
                            <TableCell>
                              <Chip 
                                label={intervention.date ? format(intervention.date, 'dd/MM', { locale: fr }) : 'N/A'}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Chip 
                              icon={<AccessTimeIcon />}
                              label={intervention.date ? format(intervention.date, 'HH:mm', { locale: fr }) : 'N/A'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {intervention.title}
                            </Typography>
                            {intervention.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {intervention.description.substring(0, 50)}{intervention.description.length > 50 ? '...' : ''}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationOnIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {intervention.location}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {intervention.technicianName || 'Non assigné'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={`${intervention.duration} min`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handlePrintIntervention(intervention)}
                              title="Imprimer"
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Aucune intervention planifiée pour cette date
                </Typography>
              )
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog pour planifier une intervention */}
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Planifier une intervention pour le {format(selectedDate, 'dd MMMM yyyy', { locale: fr })}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={[
                  ...INTERVENTION_TYPES,
                  ...workRequests.map(req => {
                    const requesterName = req.createdByName || req.requestingServiceName || 'Demandeur inconnu';
                    const description = req.description || req.problem || 'Sans description';
                    return `${requesterName} - ${description}`;
                  })
                ]}
                groupBy={(option) => {
                  if (INTERVENTION_TYPES.includes(option)) {
                    return 'Types d\'interventions';
                  }
                  return 'Demandes en cours';
                }}
                value={formData.title || ''}
                onChange={(event, newValue) => {
                  // Vérifier si une demande en cours a été sélectionnée
                  if (newValue && !INTERVENTION_TYPES.includes(newValue)) {
                    // Trouver la demande correspondante par le nom du demandeur et la description
                    const selectedRequest = workRequests.find(req => {
                      const requesterName = req.createdByName || req.requestingServiceName || 'Demandeur inconnu';
                      const description = req.description || req.problem || 'Sans description';
                      return newValue === `${requesterName} - ${description}`;
                    });
                    
                    if (selectedRequest) {
                      // Pré-remplir les champs avec les données de la demande
                      let locationText = '';
                      if (selectedRequest.location) {
                        if (typeof selectedRequest.location === 'string') {
                          locationText = selectedRequest.location;
                        } else {
                          // Construire le texte de localisation depuis l'objet
                          const parts = [];
                          if (selectedRequest.location.building) parts.push(selectedRequest.location.building);
                          if (selectedRequest.location.floor) parts.push(`Étage ${selectedRequest.location.floor}`);
                          if (selectedRequest.location.room) parts.push(`Salle ${selectedRequest.location.room}`);
                          locationText = parts.join(', ') || 'Non spécifié';
                        }
                      } else if (selectedRequest.locationText) {
                        locationText = selectedRequest.locationText;
                      }
                      
                      setFormData(prev => ({
                        ...prev,
                        title: newValue || '',
                        location: locationText,
                        workRequestId: selectedRequest.id,
                        description: selectedRequest.description || selectedRequest.problem || ''
                      }));
                      return;
                    }
                  }
                  
                  // Si ce n'est pas une demande en cours, juste mettre à jour le titre
                  setFormData(prev => ({
                    ...prev,
                    title: newValue || ''
                  }));
                }}
                inputValue={formData.title || ''}
                onInputChange={(event, newInputValue) => {
                  setFormData(prev => ({
                    ...prev,
                    title: newInputValue || ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name="title"
                    label="Titre de l'intervention"
                    fullWidth
                    required
                    helperText="Sélectionnez un type d'intervention, une demande en cours ou saisissez un titre personnalisé"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="time"
                label="Heure"
                type="time"
                fullWidth
                required
                value={formData.time}
                onChange={handleChange}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 300, // 5 minutes
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="duration"
                label="Durée (minutes)"
                type="number"
                fullWidth
                required
                value={formData.duration}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="location"
                label="Lieu de l'intervention"
                fullWidth
                required
                value={formData.location}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="equipment"
                label="Équipement/Matériel nécessaire"
                fullWidth
                value={formData.equipment}
                onChange={handleChange}
                helperText="Outils, pièces ou équipements requis pour l'intervention"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="technicianName"
                label="Nom du technicien"
                fullWidth
                required
                value={formData.technicianName}
                onChange={handleChange}
                helperText="Nom du technicien qui effectuera l'intervention"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                multiline
                rows={4}
                fullWidth
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Planifier'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterventionCalendar;
